import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Flex, Modal, P, Tag } from '@decisiv/ui-components';
import Upload from '@decisiv/iconix/lib/components/Upload';
import { t, Trans } from '@lingui/macro';

import noop from 'lodash/noop';
import WrapperWithLoading from '../WrapperWithLoading';
import IntegrationsApi from '../../api/integrations';
import fileToJson from '../../utils/fileToJson';
import { Dropzone, ResultRow } from './style';

const EXPORT_FORMAT = 'inf.integration/v1';
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 200; // ~5 minutes at POLL_INTERVAL_MS — a safety cap, not an expected duration
const SETTLED_STATUSES = ['completed', 'failed'];

const STAGES = {
  DROPZONE: 'dropzone',
  PREVIEW: 'preview',
  PROCESSING: 'processing',
  RESULTS: 'results',
  ERROR: 'error',
};

// Maps a preview/commit result row to a status pill. Preview statuses are
// prefixed `will_*` (dry-run projections); commit statuses are the settled
// outcome, so the same reasons render slightly different copy.
function describeResult({ status, reason }) {
  if (status === 'created' || status === 'will_create') {
    return {
      text: status === 'created' ? t`Created` : t`Will create`,
      color: 'success',
    };
  }

  if (status === 'skipped' || status === 'will_skip') {
    return {
      text:
        status === 'skipped'
          ? t`Skipped — already exists`
          : t`Already exists — will skip`,
      color: 'licoriceMousse',
    };
  }

  const reasonLabels = {
    action_not_found: t`Action not found`,
    invalid_category: t`Invalid category`,
  };
  const label = reasonLabels[reason] || reason || t`Error`;

  return {
    text: status === 'error' ? label : t`${label} — will error`,
    color: 'danger',
  };
}

// The commit endpoint only ever returns a tracking row (status + raw
// per-item results) — no precomputed created/skipped/errored summary, since
// that lives entirely in the async job's response, not the polled resource.
// Tally it here instead of asking the backend to store a second copy.
function summarize(results) {
  return results.reduce(
    (counts, item) => ({
      created: counts.created + (item.status === 'created' ? 1 : 0),
      skipped: counts.skipped + (item.status === 'skipped' ? 1 : 0),
      errored:
        counts.errored +
        (item.status !== 'created' && item.status !== 'skipped' ? 1 : 0),
    }),
    { created: 0, skipped: 0, errored: 0 },
  );
}

// Falls back to a generic message only when the server didn't send a
// specific one — e.g. a batch-size cap violation already explains itself
// ("Cannot process more than 15 integrations...") and shouldn't be masked.
function errorMessageFrom(errors, fallback) {
  return errors?.[0]?.detail || fallback;
}

export default function ImportIntegrationsModal({
  visible,
  onClose,
  onImported = noop,
}) {
  const [stage, setStage] = useState(STAGES.DROPZONE);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [bundle, setBundle] = useState(null);
  const [previewResults, setPreviewResults] = useState([]);
  const [commitResults, setCommitResults] = useState(null);
  const inputRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const activeImportIdRef = useRef(null);

  const clearPoll = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (visible) {
      clearPoll();
      activeImportIdRef.current = null;
      setStage(STAGES.DROPZONE);
      setLoading(false);
      setErrorMessage('');
      setBundle(null);
      setPreviewResults([]);
      setCommitResults(null);
    }
    // Closing the modal doesn't cancel the import job itself — it already
    // runs server-side regardless — but it does stop this component from
    // polling into the void once nobody's watching.
    return clearPoll;
  }, [visible, clearPoll]);

  const handleClose = () => {
    clearPoll();
    onClose();
  };

  const handleBrowse = () => {
    inputRef.current.click();
  };

  const pollImportStatus = useCallback(
    (importId, attempt = 0) => {
      IntegrationsApi.fetchImportStatus(importId).then(({ data, errors }) => {
        // The modal may have been closed/reset (or a newer import started)
        // while this request was in flight — a stale response must not
        // clobber state for whatever's active now.
        if (activeImportIdRef.current !== importId) return;

        if (errors || !data) {
          setErrorMessage(
            t`Something went wrong checking the import's progress. Please try again.`,
          );
          setStage(STAGES.ERROR);
          return;
        }

        if (!SETTLED_STATUSES.includes(data.attributes.status)) {
          if (attempt + 1 >= MAX_POLL_ATTEMPTS) {
            setErrorMessage(
              t`This import is taking longer than expected. It's still running — check back later, or contact an admin if it doesn't finish.`,
            );
            setStage(STAGES.ERROR);
            return;
          }

          pollTimeoutRef.current = setTimeout(
            () => pollImportStatus(importId, attempt + 1),
            POLL_INTERVAL_MS,
          );
          return;
        }

        setCommitResults({
          meta: summarize(data.attributes.results),
          results: data.attributes.results,
        });
        setStage(STAGES.RESULTS);
        onImported();
      });
    },
    [onImported],
  );

  const processFile = async (file) => {
    if (file.size > MAX_FILE_SIZE) {
      setErrorMessage(t`${file.name} exceeds the max file size of 5MB.`);
      setStage(STAGES.ERROR);
      return;
    }

    const parsed = await fileToJson(file);
    if (
      !parsed ||
      parsed?.meta?.format !== EXPORT_FORMAT ||
      !Array.isArray(parsed?.data)
    ) {
      setErrorMessage(
        t`This file isn't a valid integration export. Choose a file exported from another environment's Integrations page.`,
      );
      setStage(STAGES.ERROR);
      return;
    }

    setLoading(true);
    setBundle(parsed);
    const { data, errors } = await IntegrationsApi.importIntegrations(parsed, {
      preview: true,
    });

    if (errors || !data) {
      setErrorMessage(
        errorMessageFrom(
          errors,
          t`Something went wrong previewing this import. Please try again.`,
        ),
      );
      setStage(STAGES.ERROR);
      setLoading(false);
      return;
    }

    setPreviewResults(data.results ?? []);
    setStage(STAGES.PREVIEW);
    setLoading(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    inputRef.current.value = '';
    if (!file) return;

    await processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;

    await processFile(file);
  };

  const handleImport = async () => {
    setLoading(true);
    const { data, errors } = await IntegrationsApi.importIntegrations(bundle);

    if (errors || !data) {
      setErrorMessage(
        errorMessageFrom(
          errors,
          t`Something went wrong importing these integrations. Please try again.`,
        ),
      );
      setStage(STAGES.ERROR);
      setLoading(false);
      return;
    }

    setLoading(false);
    setStage(STAGES.PROCESSING);
    activeImportIdRef.current = data.id;
    pollImportStatus(data.id);
  };

  const modalTitleByStage = {
    [STAGES.DROPZONE]: t`Import Integrations`,
    [STAGES.PREVIEW]: t`Preview Import`,
    [STAGES.PROCESSING]: t`Importing…`,
    [STAGES.RESULTS]: t`Import Results`,
    [STAGES.ERROR]: t`Import Integrations`,
  };

  const actionsByStage = {
    [STAGES.DROPZONE]: [{ text: t`Cancel`, onClick: handleClose }],
    [STAGES.PREVIEW]: [
      { text: t`Import`, onClick: handleImport, disabled: loading },
      { text: t`Cancel`, onClick: handleClose },
    ],
    [STAGES.PROCESSING]: [{ text: t`Cancel`, onClick: handleClose }],
    [STAGES.RESULTS]: [{ text: t`Done`, onClick: handleClose }],
    [STAGES.ERROR]: [
      {
        text: t`Choose Another File`,
        onClick: () => setStage(STAGES.DROPZONE),
      },
      { text: t`Cancel`, onClick: handleClose },
    ],
  };

  return (
    <>
      <Modal
        actions={actionsByStage[stage]}
        icon={Upload}
        color="information"
        onClose={handleClose}
        title={modalTitleByStage[stage]}
        visible={visible}
      >
        <WrapperWithLoading
          loading={loading || stage === STAGES.PROCESSING}
          description={
            stage === STAGES.PROCESSING ? (
              <Trans>Creating your integrations — this can take a moment</Trans>
            ) : (
              <Trans>Working on it</Trans>
            )
          }
        >
          <Flex flexDirection="column" marginTop={1}>
            {stage === STAGES.DROPZONE && (
              <Dropzone
                data-testid="import-dropzone"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <P marginBottom={1}>
                  <Trans>
                    Drag an export file here, or browse for a file exported from
                    another environment&apos;s Integrations page.
                  </Trans>
                </P>
                <Button
                  text={t`Browse`}
                  icon={Upload}
                  kind="secondary"
                  size="small"
                  onClick={handleBrowse}
                />
              </Dropzone>
            )}

            {stage === STAGES.ERROR && (
              <Alert intent="danger" title={errorMessage} />
            )}

            {stage === STAGES.PREVIEW && bundle && (
              <>
                <P size="small" color="alaskanHusky" marginBottom={1}>
                  <Trans>
                    Exported from {bundle.meta?.exported_from || 'unknown'} by{' '}
                    {bundle.meta?.exported_by || 'unknown'} ·{' '}
                    {bundle.meta?.count ?? previewResults.length} integrations ·
                    secrets not included
                  </Trans>
                </P>
                <Flex flexDirection="column">
                  {previewResults.map((item) => {
                    const pill = describeResult(item);
                    return (
                      <ResultRow key={item.name}>
                        <P weight="semibold" marginBottom={0}>
                          {item.name}
                        </P>
                        <Tag
                          text={pill.text}
                          color={pill.color}
                          variant="outline"
                        />
                      </ResultRow>
                    );
                  })}
                </Flex>
              </>
            )}

            {stage === STAGES.RESULTS && commitResults && (
              <>
                <P marginBottom={1}>
                  <Trans>
                    {commitResults.meta?.created ?? 0} created ·{' '}
                    {commitResults.meta?.skipped ?? 0} skipped ·{' '}
                    {commitResults.meta?.errored ?? 0} errored
                  </Trans>
                </P>
                <Flex flexDirection="column" marginBottom={1}>
                  {(commitResults.results ?? []).map((item) => {
                    const pill = describeResult(item);
                    return (
                      <ResultRow key={item.name}>
                        <P weight="semibold" marginBottom={0}>
                          {item.name}
                        </P>
                        <Tag
                          text={pill.text}
                          color={pill.color}
                          variant="outline"
                        />
                      </ResultRow>
                    );
                  })}
                </Flex>
                <Alert
                  intent="information"
                  title={t`Imported integrations arrive disabled`}
                >
                  <Trans>
                    They have no configured account (SRM) yet and blank
                    settings. Add a configured account and fill in the
                    environment-specific URLs and secrets before enabling.
                  </Trans>
                </Alert>
              </>
            )}
          </Flex>
        </WrapperWithLoading>
      </Modal>

      <input
        type="file"
        data-testid="import-file-input"
        ref={inputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept="application/json,.json"
      />
    </>
  );
}

ImportIntegrationsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImported: PropTypes.func,
};
